import React, { useEffect, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { ToastContainer } from 'react-toastify';
import { useFormControls } from '@features/onboarding/hooks/useFormControls';
import { useTourStore } from 'src/store/tour';
import { useAuthStore } from 'src/store/auth';
import { useApplicationStore } from 'src/store/application';
import { useAcceptanceChangeStore } from 'src/store/acceptance-change';
import {
  AfterSignIn,
  TourGuide,
  AcceptanceWarningModal,
} from '@features/onboarding/components/shared/modal';
import { OnboardingSchema } from '@features/onboarding/schema/onboarding';
import { transformApplicationToOnboardingData } from '@features/onboarding/utils/transformApplicationToOnboardingData';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useInsuranceStore } from 'src/store/insurance';
import { Application } from 'src/services/interfaces/application';
import { useGeneralStore } from 'src/store';
import {
  getGoogleTrackingKey,
  hasGoogleTracking,
  pushGoogleFormPageUpdate,
} from 'src/hooks/useBranding';
import { useIsW2Mode } from '@features/w2/store/w2-mode';
import { DevFillButton, DEVFILL_TID } from 'src/devtools/DevFillButton';
import { DEVFILL_ENABLED } from 'src/devtools/gate';
import { fillOnboarding } from 'src/devtools/fillers/onboarding';

import { cn } from '@utils/index';

const MAIN_FORM_SCREEN_TRACKING: Record<
  string,
  { screenPath: string; screenName: string; pageTitle?: string }
> = {
  welcome: {
    screenPath: 'welcome',
    screenName: 'Welcome',
    pageTitle: 'Form: Welcome',
  },
  dependents: {
    screenPath: 'personal-data',
    screenName: 'Personal Data',
    pageTitle: 'Form: Personal Data',
  },
  health_plans: {
    screenPath: 'plans/medical',
    screenName: 'Plans: Medical',
    pageTitle: 'Form: Medical Plans',
  },
  approved: {
    screenPath: 'underwriting',
    screenName: 'Underwriting',
    pageTitle: 'Form: Underwriting',
  },
};

export const OnboardingForm = () => {
  const isW2 = useIsW2Mode();
  const {
    selectedStep,
    currentFormStep,
    formSteps,
    handleNextStep,
    setData,
    setTotalSteps,
  } = useFormControls();

  const { updateTierFromApplicationState, ensureFormDraftApplication, isLoading: isApplicationLoading } =
    useApplicationStore();

  const { isTourGuideOpen, openTourGuide, checkShouldShowTour } =
    useTourStore();
  const { isAuthenticated } = useAuthStore();
  const { signedAt } = useApplicationStore();
  const tracking = useGeneralStore((s) => s.siteConfig?.tracking);
  const trackingKey = getGoogleTrackingKey(tracking);
  const trackedFormScreenRef = React.useRef<string | null>(null);
  const {
    isModalOpen: isAcceptanceWarningModalOpen,
    checkApplicationStatus,
    setModalOpen: setAcceptanceWarningModalOpen,
  } = useAcceptanceChangeStore();
  const StepComponent = currentFormStep?.Component;
  const isLastStep = selectedStep === formSteps?.length - 1;

  // Flag to prevent navigation logic from triggering on every step change
  // Use sessionStorage to persist across re-renders/re-mounts
  const SESSION_KEY = 'onboarding_navigation_checked';
  const hasCheckedInitialState = () => {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  };
  const setHasCheckedInitialState = (value: boolean) => {
    if (value) {
      sessionStorage.setItem(SESSION_KEY, 'true');
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  };

  // remember data when user change step
  const handleChange = React.useCallback(
    (data: Partial<OnboardingSchema>) => {
      setData(data, { skipFormReset: true });
    },
    [setData]
  );

  const handleSubmitForm = async (data: Partial<OnboardingSchema>) => {
    setData(data);
  };

  function getFirstErrorMessage(obj: FieldErrors): string | null {
    if (typeof obj !== 'object' || obj === null) {
      return null;
    }

    if (obj?.message) {
      return obj.message as unknown as string;
    }

    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return null;
    }

    const firstKey = keys[0];
    const firstValue = obj[firstKey];

    if (typeof firstValue === 'object' && firstValue !== null) {
      const message = getFirstErrorMessage(firstValue as FieldErrors);
      if (message) {
        return message;
      }
    }

    return null;
  }
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  // const handleInvalid = (errors: FieldErrors) => {
  //   const errorMessage = getFirstErrorMessage(errors);
  //   if (errorMessage) {
  //     toast.error(errorMessage);
  //   }
  // };
  const handleInvalid = (errors: FieldErrors) => {
    const errorMessage = getFirstErrorMessage(errors);
    setFormErrorMessage(errorMessage || null);
  };
  useEffect(() => {
    setTotalSteps(formSteps.length);
  }, [formSteps.length, setTotalSteps]);

  useEffect(() => {
    const screen =
      currentFormStep?.id && MAIN_FORM_SCREEN_TRACKING[currentFormStep.id];
    if (!screen || !hasGoogleTracking(tracking)) return;

    const screenKey = `${trackingKey}:${screen.screenPath}`;
    if (trackedFormScreenRef.current === screenKey) return;

    pushGoogleFormPageUpdate(tracking, screen);
    trackedFormScreenRef.current = screenKey;
  }, [currentFormStep?.id, tracking, trackingKey]);

  // Reset the flag if user is not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setHasCheckedInitialState(false);
    }
  }, [isAuthenticated]);

  // Check application state for phqUnderReviewAt and phqAcceptedAt
  // Also handle navigation on page load to prevent form getting stuck
  useEffect(() => {
    const checkApplicationState = async () => {
      // W2 mode reuses the B2C onboarding but has no B2C application: skip the
      // tier/underwriting read AND its B2C navigation branch entirely, matching
      // handleNextClick's guard (the store read also no-ops via guardB2CSideEffect).
      if (isW2) return;
      // Only check once on initial load to prevent unwanted navigation on every step change
      if (hasCheckedInitialState()) {
        return;
      }

      try {
        await updateTierFromApplicationState();
        const { phqAcceptedAt, phqRejectedAt, phqUnderReviewAt } =
          useApplicationStore.getState();

        // Check if application is accepted (similar to under-review logic)
        if (phqAcceptedAt || phqRejectedAt || phqUnderReviewAt) {
          checkApplicationStatus(selectedStep === 1);
        }

        // BUG FIX: Handle navigation on page load if user has already completed PHQ
        // Prevent form from getting stuck after PHQ on reload
        if (phqAcceptedAt && !signedAt) {
          // User has accepted tier but hasn't signed - ensure they're on Health Plans or later
          const currentStep = formSteps[selectedStep];
          if (
            currentStep?.id === 'phq' ||
            currentStep?.sectionName === 'Health Questions'
          ) {
            // User is stuck on PHQ step - move them to Health Plans
            handleNextStep({});
            setHasCheckedInitialState(true);
            setTimeout(() => {}, 500);
            return;
          }
        } else if (phqRejectedAt && !signedAt) {
          // User is rejected - only auto-navigate if stuck on PHQ, NOT from health_plans
          const currentStep = formSteps[selectedStep];
          if (currentStep?.id === 'phq') {
            // Only skip from PHQ step, not from health_plans
            handleNextStep({});
            setHasCheckedInitialState(true);
            setTimeout(() => {}, 500);
            return;
          }
        }

        // Mark as checked even if no navigation occurred
        setHasCheckedInitialState(true);
      } catch (error) {
        console.error('Failed to check application state:', error);
        setHasCheckedInitialState(true);
      }
    };

    if (isAuthenticated) {
      checkApplicationState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, signedAt]);

  useEffect(() => {
    if (!isAuthenticated || isApplicationLoading) return;

    let cancelled = false;

    const ensureDraft = async () => {
      const created = await ensureFormDraftApplication();
      if (cancelled || !created) return;

      const { applications } = useApplicationStore.getState();
      const plans = useInsuranceStore.getState().plans;
      const draft = applications[applications.length - 1];

      if (!draft || plans.length === 0) return;

      const transformedData = transformApplicationToOnboardingData(
        draft as Application,
        plans
      );
      useOnboardingStore.getState().setData(transformedData);
      useOnboardingStore.getState().resetAllForms(transformedData);
    };

    ensureDraft();

    return () => {
      cancelled = true;
    };
  }, [
    isAuthenticated,
    isApplicationLoading,
    ensureFormDraftApplication,
  ]);

  // Show tour guide after authentication and if conditions are met
  useEffect(() => {
    // W2 mode reuses the B2C onboarding but skips the B2C product tour (it points
    // at plans / AI features that are out of scope for the W2 questionnaire).
    if (!isW2 && isAuthenticated && checkShouldShowTour()) {
      // Small delay to ensure the page is fully loaded
      const timer = setTimeout(() => {
        openTourGuide();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [isW2, isAuthenticated, checkShouldShowTour, openTourGuide]);

  return (
    <div
      id="step-parent"
      // W2 runs inside a fixed-viewport shell whose <main> is the scroller. As a
      // flex item of the `min-h-full` section this fills the scrollport when the
      // step is short and grows past it when the step is tall (`min-height:auto`
      // floor) — and either way its used height is DEFINITE, so the downstream
      // `h-full` panel/image still resolve. `min-h-full` here would make them
      // indefinite and the welcome image would size to its natural height
      // instead of cropping. No clip, or grown content is cut off. B2C keeps the
      // pinned box.
      className={cn(
        'relative max-md:mb-[100px] max-2xl:mb-[82px]',
        isW2 ? 'flex flex-1 flex-col 2xl:min-h-0' : 'h-full overflow-y-clip'
      )}
    >
      {StepComponent && (
        <StepComponent
          title={currentFormStep?.title}
          sectionName={currentFormStep?.sectionName}
          description={currentFormStep?.description}
          subscription={currentFormStep?.subscription}
          onSubmit={isLastStep ? handleSubmitForm : handleNextStep}
          onChange={handleChange}
          onInvalid={handleInvalid}
          externalErrorMessage={formErrorMessage}
          defaultStep={currentFormStep?.defaultStep}
        />
      )}
      {/* DevFill: one click fills the whole applicants + PHQ dataset with valid
          generated data (fans out via setData). The DEVFILL_ENABLED wrapper is
          what strips the button + filler + its dev deps from bundles where
          DevFill is off (see src/devtools/gate.ts). */}
      {DEVFILL_ENABLED && (
        <DevFillButton
          testid={DEVFILL_TID.onboarding}
          label="Fill onboarding"
          onFill={() => fillOnboarding()}
        />
      )}
      <ToastContainer />
      {isTourGuideOpen && <TourGuide />}
      {isAcceptanceWarningModalOpen &&
        (signedAt === null ? <AcceptanceWarningModal /> : <AfterSignIn />)}
    </div>
  );
};
